import Vue from 'vue'
import { S, G, M, A, PdGroupList, PdState, PdListState } from './types'
import { Getters, Mutations, Actions } from 'vuex'
import { normalize } from 'normalizr'
import { pdSchema } from '@/schema/pd'
import { PdEntities } from '@/schema/pd/types'
import { PdListResponse } from '@/api/pd/types'
import dayjs from 'dayjs'

/* -------------------------------------------------------------------------- */

const state = (): S => ({
  pd: {},
  sort: [],
  isInAppBrowser: false
})

/* -------------------------------------------------------------------------- */

const group = (
  pd: PdListState,
  getters: G,
  idGenerator: (pd: PdState) => string
) => {
  return getters.sortByEntryDatetime.reduce((groups, id) => {
    const p = pd[id]
    const groupId = idGenerator(p)

    if (!groups[groupId]) {
      groups[groupId] = []
    }

    groups[groupId].push(p)

    return groups
  }, {} as PdGroupList)
}

const getters: Getters<S, G> = {
  pd: state => state.pd,
  sort: state => state.sort,
  sortByEntryDatetime: ({ sort, pd }) =>
    sort.sort(
      (id1, id2) => pd[id1].entryDayjs.unix() - pd[id2].entryDayjs.unix()
    ),
  pdAll: ({ pd }, getters) =>
    group(
      pd,
      getters,
      (pd: PdState) =>
        `${pd.entryDayjs.format('YYYYMMDDHHmmss')}${(
          '00' + String(pd.id)
        ).slice(-3)}`
    ),
  pdDaily: ({ pd }, getters) =>
    group(pd, getters, (pd: PdState) => pd.entryDayjs.format('YYYYMMDD')),
  isInAppBrowser: state => state.isInAppBrowser
}

/* -------------------------------------------------------------------------- */

const mutations: Mutations<S, M> = {
  setPd: (state, { pd, sort }) => {
    state.pd = pd
    state.sort = sort
  },
  setIsInAppBrowser: (state, inapp) => {
    state.isInAppBrowser = inapp
  }
}

/* -------------------------------------------------------------------------- */

const actions: Actions<S, A, G, M> = {
  async fetch({ commit }, { patientId, year, month }) {
    const response = await Vue.$api.pd.fetch({ patientId, year, month })
    const pds = response.map(pd => ({
      ...pd,
      entryDayjs: dayjs(pd.entryDatetime)
    }))

    const { result, entities } = normalize<
      PdListResponse,
      PdEntities,
      number[]
    >(pds, [pdSchema])

    const { pd } = entities

    commit('setPd', { pd, sort: result })
  },
  setIsInAppBrowser: ({ commit }, inapp) => {
    commit('setIsInAppBrowser', inapp)
  }
}

/* -------------------------------------------------------------------------- */

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
